import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Users,
  ShieldCheck,
  MapPin,
  Compass,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Mail,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';
import { CameroonLogo } from './Logo';
import { CameroonFlag } from './CameroonFlag';
import { useLanguage } from '../context/LanguageContext';
import { getActiveSocialLinks } from '../config/social';
import { getLocalizedEquivalentUrl } from '../lib/routes';
import { Property } from '../types';
import { CameroonRegionalDirectory } from './CameroonRegionalDirectory';

interface FooterProps {
  properties?: Property[];
}

export const Footer: React.FC<FooterProps> = ({ properties = [] }) => {
  const { language, t } = useLanguage();
  const isFr = language === 'fr';

  const [email, setEmail] = useState('');
  const [newsletterHp, setNewsletterHp] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    // Anti-Bot Honeypot Defense: Silent drop
    if (newsletterHp.trim().length > 0) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 4000);
      return;
    }

    const cleanEmail = email.trim();
    if (!cleanEmail) return;

    setIsSubscribing(true);
    try {
      const { subscribeNewsletter } = await import('../lib/firestoreService');
      await subscribeNewsletter(cleanEmail, language);
      setSubscribed(true);
      setEmail('');
      setTimeout(() => {
        setSubscribed(false);
      }, 6000);
    } catch (err) {
      console.warn('Notice subscribing email to newsletter:', err);
      // Fallback display
      setSubscribed(true);
      setEmail('');
      setTimeout(() => {
        setSubscribed(false);
      }, 6000);
    } finally {
      setIsSubscribing(false);
    }
  };

  const footerNavSections = [
    {
      title: isFr ? 'Parcourir les Biens' : 'Browse Properties',
      links: [
        { label: isFr ? 'Acheter Terrain au Cameroun' : 'Buy Land in Cameroon', to: '/land-for-sale' },
        { label: isFr ? 'Terrains à Louer' : 'Land for Rent', to: '/land-for-rent' },
        { label: isFr ? 'Maisons à Vendre' : 'Houses for Sale', to: '/houses-for-sale' },
        { label: isFr ? 'Appartements à Louer' : 'Apartments for Rent', to: '/apartments-for-rent' },
        { label: isFr ? 'Studios Meublés & Suites' : 'Furnished Studios & Suites', to: '/studios-for-rent' },
        { label: isFr ? 'Hôtels & Courts Séjours' : 'Hotels & Short Stays', to: '/hotels' },
        { label: isFr ? 'Immobilier Commercial' : 'Commercial Real Estate', to: '/commercial-for-sale' },
      ],
    },
    {
      title: isFr ? 'Régions & Villes Phares' : 'Top Regions & Cities',
      links: [
        { label: isFr ? 'Annuaire des Villes du Cameroun' : 'Cameroon Cities Directory', to: '/cities' },
        { label: isFr ? 'Yaoundé (Bastos, Mendong, Efoulan)' : 'Yaoundé (Bastos, Efoulan, Mendong)', to: '/properties-for-sale/yaounde' },
        { label: isFr ? 'Douala (Bonanjo, Bonamoussadi, Akwa)' : 'Douala (Bonanjo, Bonamoussadi, Akwa)', to: '/properties-for-sale/douala' },
        { label: isFr ? 'Buea (Molyko, GRA, Clerks)' : 'Buea (Molyko, GRA, Clerks Quarters)', to: '/properties-for-sale/buea' },
        { label: isFr ? 'Limbe (Bota, Down Beach, Mile 4)' : 'Limbe (Bota, Down Beach, Mile 4)', to: '/properties-for-sale/limbe' },
        { label: isFr ? 'Kribi (Grand Batanga, Bord de Mer)' : 'Kribi (Grand Batanga, Beachfront)', to: '/properties-for-sale/kribi' },
        { label: isFr ? 'Bafoussam & Région Ouest' : 'Bafoussam & West Region', to: '/properties-for-sale/bafoussam' },
        { label: isFr ? 'Carte Cadastrale des 10 Régions' : 'All 10 Regions Cadastral Map', to: '/map' },
      ],
    },
    {
      title: isFr ? 'Légalité & Vérification' : 'Legal & Verification',
      links: [
        { label: isFr ? 'Vérifier un Titre Foncier' : 'Verify Land Title (Titre Foncier)', to: '/verify-property' },
        { label: isFr ? 'Guide Titre Foncier & MINDCAF' : 'Titre Foncier & MINDCAF Guide', to: '/resources/titre-foncier-mindcaf' },
        { label: isFr ? 'Signaler Fraude & Arnaque' : 'Check Property Scam & Report', to: '/resources/anti-scam-checklist' },
        { label: isFr ? 'Annuaire des Notaires' : 'Registered Notaries Directory', to: '/professionals/notaries' },
        { label: isFr ? 'Géomètres Experts' : 'Sworn Land Surveyors', to: '/professionals/surveyors' },
        { label: isFr ? 'Guide de Propriété Foncière' : 'Cameroon Land Ownership Guide', to: '/resources/land-buying-guide' },
        { label: isFr ? 'Investissements Diaspora' : 'Diaspora Property Investment', to: '/resources/diaspora-investment' },
      ],
    },
    {
      title: isFr ? 'Plateforme & Support' : 'Company & Support',
      links: [
        { label: isFr ? 'À Propos de Cameroon Land Market' : 'About Cameroon Land Market', to: '/about' },
        { label: isFr ? 'Tableau de Bord du Compte' : 'User Account Dashboard', to: '/dashboard' },
        { label: isFr ? 'Déposer une Annonce Gratuite (0 FCFA)' : 'Post Free Ad (0 XAF)', to: '/post-property' },
        { label: isFr ? 'Gérer Mes Annonces & Prospects' : 'Manage My Listings & Leads', to: '/my-listings' },
        { label: isFr ? 'Centre d’Aide & FAQ' : 'Help Center & FAQs', to: '/faq' },
        { label: isFr ? 'Nous Contacter' : 'Contact Us', to: '/contact' },
        { label: isFr ? 'Politique de Confidentialité' : 'Privacy Policy', to: '/privacy' },
        { label: isFr ? 'Conditions d’Utilisation' : 'Terms of Service', to: '/terms' },
      ],
    },
  ];

  return (
    <footer className="w-full bg-[#080d14] text-white border-t border-slate-800 font-sans pb-20 lg:pb-0">
      {/* 1. TOP STATS BAR */}
      <div className="border-b border-slate-800/80 bg-[#060a10]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            {/* Branding & Mission */}
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left shrink-0">
              <Link
                to={isFr ? '/fr' : '/en'}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center cursor-pointer transition-transform duration-150 active:scale-95 focus:outline-hidden"
                aria-label="Cameroon Land Market - Home"
              >
                <CameroonLogo light />
              </Link>
              <p className="text-xs text-slate-400 mt-1.5 max-w-xs leading-relaxed">
                {isFr
                  ? 'Plateforme indépendante de petites annonces immobilières pour les terrains, logements et hébergements au Cameroun.'
                  : 'Independent property-classifieds marketplace for land, housing, and accommodations across Cameroon.'}
              </p>
            </div>

            {/* 4 Grounded Regional Facts & Official Standards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 py-2 lg:py-0 border-y lg:border-y-0 lg:border-x border-slate-800/80 px-4 lg:px-8">
              {/* Stat 1: Cadastre */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-slate-900/90 border border-slate-800 flex items-center justify-center shrink-0 text-amber-400">
                  <CheckCircle className="w-4 h-4 stroke-[2]" />
                </div>
                <div className="text-left">
                  <div className="text-sm sm:text-base font-serif font-black text-white leading-tight">
                    MINDCAF
                  </div>
                  <div className="text-[11px] text-slate-400 font-medium">
                    {isFr ? 'Cadre Cadastral' : 'Cadastral Guidance'}
                  </div>
                </div>
              </div>

              {/* Stat 2: Sworn Surveyors */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-slate-900/90 border border-slate-800 flex items-center justify-center shrink-0 text-amber-400">
                  <Compass className="w-4 h-4 stroke-[2]" />
                </div>
                <div className="text-left">
                  <div className="text-sm sm:text-base font-serif font-black text-white leading-tight">
                    OGEC
                  </div>
                  <div className="text-[11px] text-slate-400 font-medium">
                    {isFr ? 'Géomètres Assermentés' : 'Sworn Surveyors'}
                  </div>
                </div>
              </div>

              {/* Stat 3: Legal Protocol */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-slate-900/90 border border-slate-800 flex items-center justify-center shrink-0 text-amber-400">
                  <ShieldCheck className="w-4 h-4 stroke-[2]" />
                </div>
                <div className="text-left">
                  <div className="text-sm sm:text-base font-serif font-black text-white leading-tight">
                    Ord. 74-1
                  </div>
                  <div className="text-[11px] text-slate-400 font-medium">
                    {isFr ? 'Cadre Légal Foncier' : 'Legal Land Protocol'}
                  </div>
                </div>
              </div>

              {/* Stat 4: National Coverage */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-slate-900/90 border border-slate-800 flex items-center justify-center shrink-0 text-amber-400">
                  <MapPin className="w-4 h-4 stroke-[2]" />
                </div>
                <div className="text-left">
                  <div className="text-sm sm:text-base font-serif font-black text-white leading-tight">
                    10 Régions
                  </div>
                  <div className="text-[11px] text-slate-400 font-medium">
                    {isFr ? 'Présence Nationale' : 'National Coverage'}
                  </div>
                </div>
              </div>
            </div>

            {/* Cameroon Diaspora Desk */}
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-900/90 border border-amber-400/40 shrink-0 shadow-sm">
              <CameroonFlag className="w-7 h-4.5 shadow-xs" />
              <div className="text-left">
                <div className="text-xs font-black text-white">
                  {isFr ? 'Bureau Diaspora Globale' : 'Global Diaspora Desk'}
                </div>
                <div className="text-[11px] text-amber-300 font-medium">
                  {isFr
                    ? 'Sécurisation investissements Europe, USA, Canada'
                    : 'Securing investments in Europe, USA, Canada'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. CAMEROON REGIONAL & CITIES SEO REAL ESTATE DIRECTORY */}
      <CameroonRegionalDirectory properties={properties} />

      {/* 3. MAIN 5-COLUMN NAVIGATION LINKS WITH CAMEROON MOUNTAIN & SEA NATURAL BACKDROP */}
      <div className="relative overflow-hidden bg-[#070b12] border-t border-slate-800/80">
        {/* Full-width Natural Landscape Background: Mount Cameroon meeting the Atlantic Ocean */}
        <picture className="absolute inset-0 w-full h-full pointer-events-none select-none">
          <source srcSet="/footer-landscape.webp" type="image/webp" />
          <img
            src="/footer-landscape.jpg"
            alt={isFr ? "Panorama naturel du Mont Cameroun et de la côte atlantique" : "Natural panorama of Mount Cameroon and the Limbe Atlantic coastline"}
            width={1376}
            height={768}
            className="w-full h-full object-cover object-center md:object-bottom opacity-75"
            loading="lazy"
            decoding="async"
          />
        </picture>

        {/* Natural Atmospheric Photographic Overlay: neutral twilight slate tone lets the authentic volcanic mountain, lush rainforest greenery, and deep blue Atlantic ocean breathe naturally */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#070b12]/80 via-[#070b12]/85 to-[#04060a]/96 pointer-events-none" />
        <div className="absolute inset-0 bg-radial from-transparent via-[#070b12]/30 to-[#04060a]/80 pointer-events-none" />

        <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 z-10">
          {/* Natural Landscape Badge and Location Indicator */}
          <div className="flex items-center justify-between gap-4 mb-8 pb-4 border-b border-slate-800/80 flex-wrap">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900/90 border border-slate-700/80 text-[11px] font-extrabold uppercase tracking-wider text-slate-200 shadow-sm backdrop-blur-xs">
              <MapPin className="w-3.5 h-3.5 text-amber-400" />
              <span>{isFr ? 'Mont Cameroun (Fako) • Océan Atlantique (Limbé)' : 'Mount Cameroon (Fako) • Atlantic Ocean (Limbe)'}</span>
            </div>
            <p className="text-[11px] sm:text-xs text-slate-400 font-medium">
              {isFr
                ? 'Des sommets volcaniques fertiles au littoral atlantique du Cameroun • Parcelles titrées & sécurisées'
                : 'From fertile volcanic slopes to Cameroon’s Atlantic coastline • Titled real estate marketplace'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {footerNavSections.map((section) => (
              <div key={section.title} className="space-y-3">
                <h4 className="text-[12px] font-black tracking-widest uppercase text-amber-400">
                  {section.title}
                </h4>
                <ul className="space-y-2">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={getLocalizedEquivalentUrl(link.to, language)}
                        className="text-xs sm:text-[13px] text-slate-300 hover:text-white transition-colors block leading-snug hover:translate-x-0.5 transform duration-150"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* 5th Column: Newsletter & Regional Support */}
            <div className="space-y-3.5">
              <h4 className="text-[12px] font-black tracking-widest uppercase text-amber-400">
                {isFr ? 'Alertes Immobilières' : 'Property Alerts'}
              </h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                {isFr
                  ? 'Abonnez-vous pour être immédiatement notifié dès qu’un terrain titré ou une opportunité foncière est publiée.'
                  : 'Subscribe to get immediate alerts whenever newly listed land parcels or property deals are published.'}
              </p>

              <form onSubmit={handleSubscribe} className="space-y-2">
                {/* Anti-Bot Honeypot */}
                <div
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    opacity: 0,
                    height: 0,
                    width: 0,
                    overflow: 'hidden',
                    pointerEvents: 'none',
                  }}
                  aria-hidden="true"
                >
                  <label htmlFor="newsletter_hp">Leave empty</label>
                  <input
                    type="text"
                    id="newsletter_hp"
                    name="newsletter_hp"
                    tabIndex={-1}
                    autoComplete="off"
                    value={newsletterHp}
                    onChange={(e) => setNewsletterHp(e.target.value)}
                  />
                </div>

                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={isFr ? 'Votre adresse email...' : 'Enter your email address...'}
                    required
                    className="w-full bg-slate-900/90 border border-slate-700/80 rounded-xl px-3.5 py-2 text-xs sm:text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubscribing}
                  className="w-full bg-amber-400 hover:bg-amber-300 disabled:bg-slate-800 disabled:text-slate-500 text-slate-950 text-xs sm:text-sm font-black py-2 rounded-xl transition-colors flex items-center justify-center gap-2 shadow-xs cursor-pointer"
                >
                  {isSubscribing ? (
                    <div className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>{isFr ? 'S’abonner aux Alertes' : 'Subscribe to Alerts'}</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              {subscribed && (
                <div className="bg-amber-400/20 border border-amber-400/40 rounded-xl p-2.5 text-xs text-amber-300 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 shrink-0" />
                  <span>
                    {isFr
                      ? 'Merci ! Vous recevrez nos nouvelles alertes vérifiées.'
                      : 'Thank you! You will receive new verified alerts.'}
                  </span>
                </div>
              )}

              {/* Offices in Cameroon */}
              <div className="pt-2 text-xs text-slate-400 space-y-1.5">
                <p className="flex items-center gap-1.5 font-medium text-slate-300">
                  <MapPin className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <span>{isFr ? 'Siège : Bastos, Av. Charles de Gaulle, B.P. 1422 Yaoundé' : 'Headquarters: Bastos, Av. Charles de Gaulle, B.P. 1422 Yaoundé'}</span>
                </p>
                <p className="flex items-center gap-1.5 font-medium text-slate-300">
                  <MapPin className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <span>{isFr ? 'Bureau Littoral : Centre d’Affaires Bonanjo, Douala' : 'Littoral Office: Bonanjo Business District, Douala'}</span>
                </p>
                <a
                  href="mailto:support@cameroonlandmarket.com"
                  className="flex items-center gap-1.5 font-medium text-slate-300 hover:text-white hover:underline transition-colors"
                >
                  <Mail className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <span>support@cameroonlandmarket.com</span>
                </a>
              </div>
            </div>
          </div>

          {/* 3. PAYMENT METHODS, ESCROW NOTICE & TRUST BAR */}
          <div className="mt-8 pt-5 border-t border-slate-800/80 flex flex-col gap-3.5 text-xs text-slate-400">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-1 rounded-full bg-amber-400 text-slate-950 font-black text-[10.5px] uppercase tracking-wider">
                  {isFr ? '✓ Annonces 100% Gratuites' : '✓ 100% Free Classifieds'}
                </span>
                <span className="font-bold text-white ml-1">
                  {isFr ? 'Sécurisation :' : 'Escrow Payments:'}
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-slate-900/90 border border-slate-800 text-[11px] font-bold text-yellow-300">
                  MTN MoMo
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-slate-900/90 border border-slate-800 text-[11px] font-bold text-orange-300">
                  Orange Money
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-slate-900/90 border border-slate-800 text-[11px] font-bold text-blue-200">
                  Visa / Mastercard
                </span>
                <span className="px-2.5 py-1 rounded-lg bg-slate-900/90 border border-slate-800 text-[11px] font-bold text-amber-300">
                  {isFr ? 'Compte Notarié Séquestre' : 'Notary Escrow Account'}
                </span>
              </div>

              {/* Social icons */}
              {(() => {
                const activeSocialLinks = getActiveSocialLinks();
                if (activeSocialLinks.length === 0) return null;
                return (
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-medium text-slate-400 mr-1">
                      {isFr ? 'Suivez-nous :' : 'Follow Us:'}
                    </span>
                    {activeSocialLinks.map((item) => {
                      let Icon = Facebook;
                      if (item.key === 'instagram') Icon = Instagram;
                      else if (item.key === 'linkedin') Icon = Linkedin;
                      else if (item.key === 'youtube') Icon = Youtube;

                      return (
                        <a
                          key={item.key}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-7 h-7 rounded-full bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-300 hover:text-white flex items-center justify-center transition-colors"
                          aria-label={isFr ? item.labelFr : item.labelEn}
                          title={isFr ? item.labelFr : item.labelEn}
                        >
                          <Icon className="w-3.5 h-3.5" />
                        </a>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Mandatory Escrow Disclaimer */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-3 text-xs text-slate-300 leading-relaxed">
              <span className="font-bold text-amber-300 block mb-0.5">
                {isFr ? 'Avis relatif au Séquestre :' : 'Escrow Notice:'}
              </span>
              {isFr
                ? 'Le séquestre, lorsqu’il est disponible, est organisé directement auprès d’un notaire identifié, d’un établissement financier ou d’un prestataire tiers autorisé. Cameroon Land Market ne conserve pas les fonds des utilisateurs.'
                : 'Escrow, when available, is arranged directly through an identified licensed notary, financial institution or authorized third-party provider. Cameroon Land Market does not hold client funds.'}
            </div>
          </div>

          {/* 4. INDEPENDENT-PLATFORM DISCLOSURE & LEGAL LINE */}
          <div className="mt-6 pt-5 border-t border-slate-800/80 space-y-3.5 text-xs text-slate-400">
            {/* Mandatory Independent-Platform Disclosure */}
            <div className="bg-slate-950/90 border border-slate-800/80 rounded-xl p-3.5 text-slate-400 leading-relaxed space-y-1.5">
              <p className="text-xs sm:text-[12.5px]">
                {isFr
                  ? 'Cameroon Land Market est une plateforme indépendante de petites annonces immobilières et n’est pas un organisme gouvernemental. Les références au MINDCAF, au cadastre, aux services fonciers, aux notaires et aux géomètres décrivent les procédures de vérification applicables et n’impliquent aucune affiliation ni approbation gouvernementale.'
                  : 'Cameroon Land Market is an independent property-classifieds marketplace and is not a government agency. References to MINDCAF, cadastral records, land registries, notaries and surveyors describe relevant verification procedures and do not imply government affiliation or endorsement.'}{' '}
                <Link to={getLocalizedEquivalentUrl('/terms', language)} className="text-amber-400 hover:text-amber-300 underline font-semibold ml-1">
                  {isFr ? 'Conditions d’Utilisation' : 'Terms of Service'}
                </Link>
              </p>
              <p className="text-[11.5px] text-slate-500">
                {isFr
                  ? 'Définition de la mention « Vérifié » : contrôle documentaire préalable du numéro de Titre Foncier et du plan de bornage géomètre déclarés par l’annonceur. Cela ne dispense pas l’acheteur d’effectuer ses vérifications auprès d’un notaire assermenté et ne constitue pas une garantie étatique d’indemnisation.'
                  : 'Definition of "Verified": preliminary administrative review of the Land Certificate (Titre Foncier) number and surveyor boundary plan provided by the listing party. This does not replace due diligence by a licensed notary and does not constitute a government indemnity guarantee.'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1 text-xs">
              <div className="text-slate-400">
                © {new Date().getFullYear()} Cameroon Land Market. {isFr ? 'Plateforme indépendante de petites annonces.' : 'Independent classifieds marketplace.'}
              </div>
              <div className="flex items-center gap-3 sm:gap-4 flex-wrap text-slate-400">
                <Link to={getLocalizedEquivalentUrl('/privacy', language)} className="hover:text-white transition-colors">
                  {isFr ? 'Confidentialité' : 'Privacy Policy'}
                </Link>
                <span>•</span>
                <Link to={getLocalizedEquivalentUrl('/terms', language)} className="hover:text-white transition-colors">
                  {isFr ? 'Conditions Générales' : 'Terms of Service'}
                </Link>
                <span>•</span>
                <Link to={getLocalizedEquivalentUrl('/resources/legal-texts-and-laws', language)} className="hover:text-white transition-colors">
                  {isFr ? 'Ordonnance 74-1' : 'Ordinance 74-1'}
                </Link>
                <span>•</span>
                <Link to={getLocalizedEquivalentUrl('/contact', language)} className="hover:text-white transition-colors">
                  {isFr ? 'Support' : 'Support Desk'}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
